import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function AboutUs() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Automigo - Business Automation Services",
    "description": "Learn how Automigo helps local service businesses turn inquiries into booked jobs with automated lead response systems.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about-us`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Automigo",
      "description": "A simple way to make sure real customers don't get missed.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "logo": "https://public.readdy.ai/ai/img_res/f48ccc04-9914-43af-8ca4-a7c20e3394e9.png"
    }
  };

  return (
    <>
      <SEOHead
        title="About Us - Automigo | Business Automation for Service Professionals"
        description="Learn how Automigo helps local service businesses turn inquiries into booked jobs with automated lead response systems and SMS automation."
        keywords="business automation, lead response, service business, automation tools"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about-us`}
        schema={schema}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between h-20">
              <div>
                <Link to="/" className="flex items-center gap-3 cursor-pointer">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                    <i className="ri-robot-2-line text-white text-xl"></i>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">Automigo</span>
                </Link>
                <p className="text-xs text-gray-600 mt-1 ml-1">Serving Long Island Businesses</p>
              </div>
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
                <Link to="/how-it-works" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">How It Works</Link>
                <Link to="/who-its-for" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Who It's For</Link>
                <Link to="/about-us" className="text-sm font-medium text-teal-600 cursor-pointer whitespace-nowrap">About</Link>
                <Link to="/get-started" className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">Get Started</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                About Automigo
              </h1>
              <p className="text-xl text-white/90">
                A simple way to make sure real customers don't get missed.
              </p>
            </div>
          </div>
        </section>

        {/* Origin Story */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Why Automigo Exists
              </h2>
              <div className="space-y-6 text-center">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Service businesses don't lose jobs because they're worse —<br />
                  they lose them because they're busy.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Customers request multiple quotes.<br />
                  The first business to respond usually wins.
                </p>
                <p className="text-lg text-teal-600 font-semibold leading-relaxed">
                  Automigo exists to make sure serious customers<br />
                  don't slip through the cracks.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Makes It Different */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                What Makes It Different
              </h2>
              <p className="text-lg text-gray-700 text-center mb-12">
                Automigo isn't another platform you have to learn.
              </p>
              
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm mb-8">
                <div className="space-y-6 text-center">
                  <p className="text-lg text-gray-700">Your customers contact you normally</p>
                  <p className="text-lg text-gray-700">You reply normally</p>
                  <p className="text-lg text-teal-600 font-semibold">Automigo handles the timing</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className="ri-dashboard-line text-teal-600 text-2xl"></i>
                  </div>
                  <p className="text-gray-700 font-medium">No complicated dashboards</p>
                </div>
                <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className="ri-refresh-line text-teal-600 text-2xl"></i>
                  </div>
                  <p className="text-gray-700 font-medium">No workflow changes</p>
                </div>
                <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-smile-line text-teal-600 text-2xl"></i>
                  </div>
                  <p className="text-gray-700 font-medium">No customer confusion</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Built for Service Businesses */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Built for Service Businesses
              </h2>
              
              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {['Contractors', 'Home services', 'Cleaning companies', 'Repair professionals', 'Local specialists'].map((type, index) => (
                  <span key={index} className="bg-teal-100 text-teal-700 px-5 py-2.5 rounded-full text-sm font-medium">
                    {type}
                  </span>
                ))}
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                You don't need more leads —<br />
                you need to capture the ones you already get.
              </p>
            </div>
          </div>
        </section>

        {/* How I Work */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                How I Work
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <p className="text-gray-700 text-lg pt-2">I connect to your existing website</p>
                </div>
                
                <div className="bg-white rounded-xl p-6 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <p className="text-gray-700 text-lg pt-2">Customize responses for your business</p>
                </div>
                
                <div className="bg-white rounded-xl p-6 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <p className="text-gray-700 text-lg pt-2">Launch within a few days</p>
                </div>
              </div>

              <p className="text-center text-lg text-teal-600 font-semibold mt-12">
                You stay in control — Automigo removes the delay.
              </p>
            </div>
          </div>
        </section>

        {/* Privacy & Compliance */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-check-line text-teal-400 text-3xl"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Commitment to Privacy &amp; Communication
                </h2>
              </div>

              <div className="space-y-6 text-center">
                <p className="text-gray-300 text-lg leading-relaxed">
                  Customers always know when and why they are contacted.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Forms include messaging disclosures and opt-out instructions.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Contact information is only used to respond to requests<br />
                  and coordinate service.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Information is never sold or used for unrelated marketing.
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-4 mt-12">
                <Link 
                  to="/terms-and-conditions" 
                  className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-text-line"></i>
                  Messaging Terms
                </Link>
                <Link 
                  to="/privacy-policy" 
                  className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-lock-line"></i>
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                The Goal
              </h2>
              <div className="space-y-4">
                <p className="text-xl text-gray-700 leading-relaxed">
                  Automigo isn't about automation.
                </p>
                <p className="text-xl text-teal-600 font-semibold leading-relaxed">
                  It's about making sure real customers get responses.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-500">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              See how it would work for your business
            </h2>
            <Link 
              to="/get-started"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get My Free Automation Plan
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-8">
              <p className="text-gray-400 text-sm">Serving businesses across Long Island, NY</p>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-robot-2-line text-white text-xl"></i>
                </div>
                <span className="text-2xl font-bold text-white">Automigo</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6">
                <Link to="/about-us" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">About</Link>
                <Link to="/how-it-works" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">How It Works</Link>
                <Link to="/get-started" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Contact</Link>
                <Link to="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Messaging Terms</Link>
                <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Privacy</Link>
              </div>
              <div className="flex items-center gap-6">
                <p className="text-sm text-gray-500">© 2025 Automigo</p>
                <a href="https://readdy.ai/?ref=logo" className="text-sm text-gray-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
